import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const links = [
  { label: "Services", href: "#services" },
  { label: "How It Works", href: "#how" },
  { label: "Our Work", href: "#work" },
  { label: "Contact", href: "#contact" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200 shadow-sm"
          : "bg-gradient-to-b from-slate-950/55 via-slate-950/25 to-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center group">
          <img src={scrolled ? "/logo.png" : "/logo-white.png"} alt="Tennessee Web & Design" className={`h-12 w-auto transition-transform duration-200 group-hover:scale-105 ${scrolled ? "" : "drop-shadow-[0_2px_6px_rgba(0,0,0,0.45)]"}`} />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm font-medium transition-colors duration-200 ${
                scrolled ? "text-slate-600 hover:text-amber-700" : "text-white/90 hover:text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]"
              }`}
            >
              {l.label}
            </a>
          ))}
          {user ? (
            <button
              data-testid="nav-dashboard-btn"
              onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")}
              className="rounded-full bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-5 py-2.5 transition-colors duration-200 shadow-sm"
            >
              Dashboard
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <Link
                to="/login"
                data-testid="nav-login-btn"
                className={`text-sm font-semibold transition-colors duration-200 ${
                  scrolled ? "text-slate-700 hover:text-amber-700" : "text-white hover:text-amber-200 drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]"
                }`}
              >
                Client Login
              </Link>
              <a
                href="/#contact"
                data-testid="nav-getstarted-btn"
                className="rounded-full bg-amber-600 hover:bg-amber-700 hover:scale-105 text-white text-sm font-semibold px-5 py-2.5 transition-all duration-200 shadow-sm"
              >
                Get Started
              </a>
            </div>
          )}
        </div>

        <button
          className={`md:hidden transition-colors duration-200 ${scrolled ? "text-slate-800" : "text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]"}`}
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden bg-white border-t border-slate-200 px-6 py-4 flex flex-col gap-4">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="text-sm font-medium text-slate-700"
            >
              {l.label}
            </a>
          ))}
          <Link to={user ? "/dashboard" : "/login"} className="text-sm font-semibold text-amber-700">
            {user ? "Dashboard" : "Client Login"}
          </Link>
        </div>
      )}
    </header>
  );
}
